/**
 * FacilityLens Zustand store
 */

import { create } from "zustand";
import type {
	FacilityLensScan,
	FacilityLensKnowledge,
	FacilityLensMessage,
	LikelyCause,
} from "@/services/facility-lens-service";

export type ResultMode = "report" | "fix";

/** Tracks progress through the symptom troubleshooting flow */
export interface TroubleshootState {
	selectedSymptomIndex: number | null;
	followUpAnswers: Record<number, string>; // questionIndex -> selected option
	diagnosis: LikelyCause | null;
	selfFixAttempted: boolean;
	selfFixResolved: boolean | null; // null = not yet answered
}

const INITIAL_TROUBLESHOOT: TroubleshootState = {
	selectedSymptomIndex: null,
	followUpAnswers: {},
	diagnosis: null,
	selfFixAttempted: false,
	selfFixResolved: null,
};

interface FacilityLensState {
	activeScan: FacilityLensScan | null;
	knowledge: FacilityLensKnowledge | null;
	messages: FacilityLensMessage[];
	loading: boolean;
	analyzing: boolean;
	selectedIssueIndices: number[];

	// Role-aware result mode
	resultMode: ResultMode;
	troubleshoot: TroubleshootState;

	setScan: (scan: FacilityLensScan | null) => void;
	setKnowledge: (knowledge: FacilityLensKnowledge | null) => void;
	setMessages: (messages: FacilityLensMessage[]) => void;
	addMessage: (msg: FacilityLensMessage) => void;
	setLoading: (loading: boolean) => void;
	setAnalyzing: (analyzing: boolean) => void;
	toggleIssue: (index: number) => void;
	setSelectedIssues: (indices: number[]) => void;

	setResultMode: (mode: ResultMode) => void;
	selectSymptom: (index: number) => void;
	answerFollowUp: (questionIndex: number, answer: string) => void;
	setDiagnosis: (cause: LikelyCause | null) => void;
	setSelfFixAttempted: (attempted: boolean) => void;
	setSelfFixResolved: (resolved: boolean | null) => void;
	resetTroubleshoot: () => void;

	reset: () => void;
}

export const useFacilityLensStore = create<FacilityLensState>((set) => ({
	activeScan: null,
	knowledge: null,
	messages: [],
	loading: false,
	analyzing: false,
	selectedIssueIndices: [],
	resultMode: "report",
	troubleshoot: { ...INITIAL_TROUBLESHOOT },

	setScan: (activeScan) => set({ activeScan }),
	setKnowledge: (knowledge) => set({ knowledge }),
	setMessages: (messages) => set({ messages }),
	addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
	setLoading: (loading) => set({ loading }),
	setAnalyzing: (analyzing) => set({ analyzing }),
	toggleIssue: (index) =>
		set((s) => {
			const indices = s.selectedIssueIndices.includes(index)
				? s.selectedIssueIndices.filter((i) => i !== index)
				: [...s.selectedIssueIndices, index];
			return { selectedIssueIndices: indices };
		}),
	setSelectedIssues: (selectedIssueIndices) => set({ selectedIssueIndices }),

	setResultMode: (resultMode) => set({ resultMode }),
	selectSymptom: (index) =>
		set({ troubleshoot: { ...INITIAL_TROUBLESHOOT, selectedSymptomIndex: index } }),
	answerFollowUp: (questionIndex, answer) =>
		set((s) => ({
			troubleshoot: {
				...s.troubleshoot,
				followUpAnswers: { ...s.troubleshoot.followUpAnswers, [questionIndex]: answer },
			},
		})),
	setDiagnosis: (cause) =>
		set((s) => ({ troubleshoot: { ...s.troubleshoot, diagnosis: cause } })),
	setSelfFixAttempted: (attempted) =>
		set((s) => ({ troubleshoot: { ...s.troubleshoot, selfFixAttempted: attempted } })),
	setSelfFixResolved: (resolved) =>
		set((s) => ({ troubleshoot: { ...s.troubleshoot, selfFixResolved: resolved } })),
	resetTroubleshoot: () => set({ troubleshoot: { ...INITIAL_TROUBLESHOOT } }),

	reset: () =>
		set({
			activeScan: null,
			knowledge: null,
			messages: [],
			loading: false,
			analyzing: false,
			selectedIssueIndices: [],
			resultMode: "report",
			troubleshoot: { ...INITIAL_TROUBLESHOOT },
		}),
}));
